// src/config/tuning.ts — DESIGN §3–§14'ün TÜM sayıları; her satır bölüm referanslı. YALNIZ WP0 yazar ("tam dolu, tek yazar").
// Başka paket eksik sayı bulursa "sürüm 2" isteği açar; burada olmayan sayı kodda yazılmaz (ARCHITECTURE değişmez 5).
// Birimler: u, tick (60 Hz), sn, derece. "(ölçüm)" = anim-metrics; "(tahmin)" = ANIMATION §9 madde 1 kapısı.
import type { Tuning } from '../core/types.ts';

export const T: Tuning = {
  MOVE: {
    runSpeed: 6.5,            // §3 koşu hızı (FT 4.9 × 1.33 boy oranı), çubuk ≥ 0.85
    runThreshold: 5.5,        // §2 koşma bağlamı: yer hızı ≥ 5.5 u/s
    runStickMin: 0.85,        // §2 koşma bağlamı: çubuk ≥ 0.85
    runContextTicks: 10,      // §2 koşma bağlamı: en az 10 t
    walkDead: 0.18,           // §3 yürüme: çubuk büyüklüğü smoothstep 0.18 → 1.0 (pad ölü bölgesiyle aynı)
    groundAccel: 45,          // §3 yer ivmesi (u/s²)
    groundFriction: 55,       // §3 sürtünme (u/s²): 0.12 s'de durur
    turnRate: 14,             // §3 dönüş 14 rad/s (r fazında da)
    snapTurnDeg: 150,         // §3 150°+ dönüşte snap
    snapTurnTicks: 5,         // §3 snap süresi 5 t
    airAccel: 18,             // §3 hava kontrolü ivmesi
    maxAirSpeed: 7,           // §3 hava azami yatay hız
    airAttackSpeedMult: 0.6,  // §4.1 A1 hava jab'da yatay hız ×0.6
    carrySpeedMult: 0.55,     // §4.1 C taşırken hız ×0.55 (3.6 u/s)
    carryTurnMult: 0.7,       // §4.1 C taşırken dönüş ×0.7
    blockSpeedMult: 0.20,     // §3 blok yürüyüşü ×0.20 = 1.3 u/s (ayak kayması yok)
    twoHandSpeedMult: 0.85,   // §9 2H hareket ×0.85
    crawlSpeed: 1.5,          // §4.1 sürünme 1.5 u/s
    walkFull: 1.0,            // §3 yürüme eğrisi üst ucu (WP0 ek)
  },
  JUMP: {
    gravity: -30,             // §3 yerçekimi −30 u/s²
    fallGravMult: 1.4,        // §3 düşerken ×1.4 (−42)
    terminal: -32,            // §3 terminal hız
    v0: 11,                   // §3 zıplama v0 = 11 → tepe 2.02 u, 0.37 s çıkış
    cutMult: 0.45,            // §3 erken bırakma vy ×0.45
    doubleV: 9,               // §3 çift zıplama vy = 9 (atar, eklemez) → +1.35 u
    coyoteTicks: 6,           // §3/§11 coyote 6 t
    bufferTicks: 7,           // §3 zıplama tamponu 7 t
    fastFallVy: -14,          // §3 hızlı iniş vy = min(vy, −14); yalnız L1 basma kenarı ∧ vy ≤ 0
    edgeCoyoteTicks: 7,       // §3/§11 kenar coyote 7 t
    landTicks: 8,             // §4 landPhase 'recover': inişte Jump_Land 8 t hareket kilidi (saldırı hemen)
  },
  DODGE: {
    ticks: 19,                // §3 Dodge_* tS 1.25 → 19 t (0.32 s)
    iframeFrom: 2, iframeTo: 12,   // §3 i-frame 2–12 t
    distance: 3.0,            // §3 kök hareket ölçekle → 3.0 u
    rate: 1.25,               // §3 tS 1.25
    lightCancelAt: 12,        // §3 12 t'den sonra hafif saldırıyla iptal
    streakPenalty: 3,         // §3 ardışık dodge: her tekrarda i-frame −3 t
    streakMinIframes: 3,      // §3 taban 3 t
    streakResetTicks: 48,     // §3 48 t dodge'suz geçince sıfırlanır
    air: {
      speed: 4.5,             // §3 hava dodge darbesi 4.5 u/s çubuk yönünde
      maxVy: 2,               // §3 dikey bileşen ≤ +2
      iframeFrom: 2, iframeTo: 12,   // §3 i-frame 2–12 t
      gravMult: 0.3,          // §3 yerçekimi ×0.3
      gravTicks: 18,          // §3 18 t boyunca
      noAttackTicks: 15,      // §3 sonra 15 t saldırı yasağı
    },
    rmWindow: [0, 0.40],      // ANIMATION §5 Dodge_* ölçekle penceresi (WP0 ek)
  },
  BLOCK: {
    enterTicks: 13,           // §3 Melee_Block [0–0.33] tS 1.5 = 0.22 s = 13 t
    guardFrom: 4,             // §3 guard 4 t'den itibaren aktif
    exitTicks: 6,             // §3 bırakınca 6 t çıkış (blok tutarken saldırı çıkışı atlar)
    coneDeg: 120,             // §3 ön koni 120°
    shieldConeDeg: 180,       // §3/§4.2 kalkanla 180°
    guardMax: 100,            // §3 guard barı 100
    shieldGuardMax: 150,      // §4.2 kalkan guard 150
    regenDelayTicks: 60,      // §3 son bloktan 1 s sonra dolar
    regenPerSec: 25,          // §3 25/s (4 s'de tam)
    breakRestart: 50,         // §3 guard break sonrası bar 50'den başlar
    breakTicks: 48,           // §3 guard break sersemliği 48 t (0.80 s)
    hitTicks: 15,             // §3 Melee_Block_Hit [0–0.5] tS 2.0 = 0.25 s = 15 t
    reblockTicks: 7,          // §3 bloklu isabetten 7 t sonra tekrar bloklar
    kbMult: 0.3,              // §3/§5/§7 bloklu isabet kb ×0.3
    stunMult: 0.5,            // §3/§5/§7 bloklu isabet stun ×0.5 (dmg 0)
    parryTapTicks: 6,         // §7 L1 ≤ 6 t'de bırakılırsa parry dokunuşu
    shieldParryTapTicks: 8,   // §4.2/§7 kalkanla ≤ 8 t
    parryWindowTicks: 6,      // §7 bırakma tick'inden 6 t parry penceresi
    parryRefractory: 30,      // §7 boşa dokunma → 30 t refrakter (parry yok, blok var)
    refractoryGuardFrom: 8,   // §7 refrakterde blok guard'ı 8 t'den itibaren
    attackFromBlockGuardCost: 0,   // §2 blok tutarken X/Y/B/R1: guard maliyeti yok
    parryBonusTicks: 120,     // §7 parry sonrası 120 t içindeki ilk isabet knockdown (tumble)
    parryVictimTicks: 40,     // §7 saldıran Hit_B tS 1.3 = 0.67 s = 40 t sersem, kb 0, i-frame yok
    chipStunShield: 0.3,      // §4.2 kalkan chip stun ×0.3
    parryFreeTicks: 15,       // §7 savunan 15 t'de serbest (WP0 ek)
    guardCostLight: 12,       // §3 hafif −12 (WP0 ek; MoveDef.guardCost varsayılanı)
    guardCostHeavy: 30,       // §3 ağır −30 (WP0 ek; Sweep 40, 2H Chop 45 hamle tablosunda)
  },
  COMBAT: {
    comboWindowTicks: 84,     // §5 n = son 84 t'deki isabet sayısı (bu isabet hariç)
    falloff: 0.85,            // §5 azalan getiri 0.85^n (stun ve kb)
    staleBase: 0.8,           // §5 bayatlama 0.8^k
    staleMin: 0.5,            // §5 bayatlama tabanı 0.5
    staleTicks: 150,          // §5 aynı hamlenin 150 t (2.5 s) içindeki tekrarı
    staleQueue: 6,            // §5 kuyruk 6 hamle
    kbDmgScale: 0.9,          // §5 kb · (1 + dmg/100 · 0.9)
    liftDmgScale: 0.5,        // §5 lift · (1 + dmg/100 · 0.5)
    kbFloor: 4.0,             // §5 kbSpeed = max(kbSpeed, 4.0) blok değilse
    diDeg: 18,                // §5 DI ±18° (yalnız isabet tick'inde)
    velCarry: 0.3,            // §5 vel.xz = dir·kbSpeed + 0.3·eski
    hitstunBase: 0.12,        // §5 hitstun = clamp(0.12 + 0.020·kbSpeed, 0.12, 0.90) s
    hitstunPerKb: 0.020,      // §5
    hitstunMin: 0.12,         // §5
    hitstunMax: 0.90,         // §5
    hitstunFalloff: 0.85,     // §5 · 0.85^max(0, n−2)
    hitstunFalloffFrom: 2,    // §5 n ≤ 2 çarpan 1 (zincir), 2v1 freni n ≥ 3
    hitstunMinTicks: 6,       // §5 taban 6 t
    tumbleKb: 9,              // §5 kbSpeed ≥ 9 → tumble
    tumbleCapBase: 9,         // §5 kbTumble = min(kbSpeed, 9 + 6·dmg/100)
    tumbleCapPerDmg: 6,       // §5
    forcedTumbleHits: 5,      // §5 n ≥ 5 (84 t'de 6. isabet) → zorunlu tumble
    knockdownStun: 100,       // §5 stun ≥ 100 → knockdown
    heavyKnockdownStun: 55,   // §5 stun_önce ≥ 55 ∧ heavy → knockdown ("!" eşiği, HUD §14)
    stunDecayDelay: 36,       // §5 son isabetten 36 t (0.6 s) sonra sönme
    stunDecay: 30,            // §5 −30/s
    tauntDecay: 40,           // §4.1 T / §5 taunt −40/s ek, gecikmesiz
    lyingDmgMult: 0.5,        // §6 yatana dmg ×0.5 (stun 0)
    lyingKbMult: 0.5,         // §6 yatana kb ×0.5 (kayma, lift 0)
    hitAKb: 5.5,              // §5 kbSpeed < 5.5 → Hit_A
    hitBKb: 9,                // §5 5.5–9 → Hit_B
    weaponDropStun: 45,       // §9 tek vuruşta stun ≥ 45 → silah düşer
    grabMaxFootHeight: 0.5,   // §4.1 G1 / §7 ayak > 0.5 u tutulamaz (hook tepesi 0.31 tutulur)
    dmgMax: 300,              // §5 dmg 0–300 (WP0 ek)
    stunMax: 100,             // §5 stun 0–100 (WP0 ek)
    lockAfterL3: 24,          // §5 L3'ten sonra 24 t X kilidi (WP0 ek)
    lockAfterHeavy: 18,       // §5 ağırlardan sonra 18 t aile kilidi (WP0 ek)
    turnToAttackerTicks: 5,   // §5 arkadan ağır darbe: kurban 5 t'de saldırana döner (WP0 ek)
    sideSnapDeg: 45,          // ANIMATION §6.1 yandan darbe: yaw en yakın 45° katına (WP0 ek)
  },
  HITSTOP: {
    light: 3,                 // §11 hafif 3 t (50 ms)
    heavy: 5,                 // §11 ağır 5 t (83 ms)
    finisher: 7,              // §11 knockdown/bitirici/fırlatma 7 t (117 ms)
    parry: 8,                 // §11 parry 8 t (133 ms)
    block: 2,                 // §11 blok 2 t (33 ms)
    // 0: ring-out'ta TÜM SAHNE donması kaldırıldı (kullanıcı isteği: "birileri düşünce oyun yavaşlıyor" —
    // 7 t = 117 ms'lik donma oyunu takılıyor gibi gösteriyordu). Vuruşların kendi hitstop'u (hafif/ağır/bitirici) duruyor.
    ringout: 0,               // §11 (eski 7 t tüm sahne)
    bodyImpact: 5,            // §6 fırlatılan beden çarpması 5 t ikisine
  },
  KNOCKDOWN: {
    fallTicks: 11,            // §6 Death_A@0.37-0.55 tS 1.0 = 11 t çakılma
    lyingTicks: 84,           // §6 Lying tabanı 84 t (1.4 s)
    lyingThrownTicks: 60,     // §6 fırlatılma sonrası 60 t
    quickGetUpAfter: 30,      // §4.1/§6 A ile hızlı kalk ≥ 30 t sonra
    getUpTicks: 48,           // §6 GetUp Lie_StandUp@0.70-2.33 tS 2.05 = 48 t, i-frame tamamı
    quickGetUpTicks: 28,      // §4.1 hızlı kalk Lie_StandUp@1.60-2.33 tS 1.6 = 28 t
    getUpKickTicks: 22,       // §4.1 kalkış tekmesi aşama 1: Lie_StandUp@1.60-2.33 tS 2.0, 0–21 t (i-frame vuruş + kaldırma)
    postGetUpIframes: 36,     // §6 kalkış sonrası 36 t i-frame (kendi saldırı/tutma girdisi bitirir)
    rollTicks: 21,            // §4.1 Roll 21 t (0.35 s), i-frame tamamı, 1×/knockdown
    rollDist: 1.3,            // §4.1 Roll 1.3 u yana
    whiffLyingTicks: 24,      // §4.1 A2 whiff: LyingFront 24 t
    whiffAutoGetUp: true,     // §4.1 A2 whiff: otomatik hızlı kalk
    whiffPostGetUpIframes: 0, // §4.1 A2 whiff: kalkış sonrası i-frame yok
    carryDropLyingL1: 30,     // §4.1 C L1 bırak → kurban Lying 30 t (0.5 s)
    carryDropLyingBash: 48,   // §4.1 C omuz çarpması isabeti → kurban Lying 48 t (0.8 s)
    carryDropLyingThirdParty: 48,  // §6 3. kişi koparması → Lying (kalan süre, en az 48 t)
    tumbleTurnsPerSec: 1.5,   // §5 tumble prosedürel dönüş 1.5 tur/s
    slideTau: 0.36,           // §5/§6 yere kayma τ 0.36 s (her tick ×0.955)
    slideStop: 0.3,           // §5/§6 |v| < 0.3 → 0
    faceplantTicks: 13,       // §4.1 A2 Death_B@1.30-1.75 tS 2.0 = 13 t (WP0 ek)
    getUpStandAt: 38,         // §6 GetUp'ta dik anı 38 t (WP0 ek)
    lyingIdleAfterTicks: 18,  // §6 Death_A_Pose → 0.3 s sonra Lie_Idle (WP0 ek)
    groundPoseSlerpTicks: 3,  // ANIMATION §6.2 tumble inişinde sırtüstüne slerp 3 t (WP0 ek)
    carryBreakLyingMin: 48,   // §6 Carry koparmasında Lying en az 48 t (WP0 ek)
  },
  TAUNT: {
    cancelAfterTicks: 9,      // §4.1 T 9 t'den sonra hareket/saldırıyla iptal
    ticks: 83,                // §4.1 T Cheering tS 1.2 = 1.39 s = 83 t (WP0 ek)
  },
  GRAB: {
    holdLockTicks: 15,        // §4.1 G1b Hold kilidi 15 t
    holdThrowInstantStun: 40, // §4.1 G1b grabStun ≥ 40 → Throw hemen; < 40 → en erken 15. t
    holdDist: 1.1,            // §4.1 G1b kurban tutanın 1.1 u önünde
    shoulderTicks: 26,        // §4.1 G1b omuza alma Interact@0.65-1.30 tS 1.5 = 26 t (FState.Lifting)
    windupEscapePresses: 4,   // §6 Hold+Throw windup (34 t) / omuza alma (26 t) içinde 4 basış
    windupEscapeMaxStun: 40,  // §6 tutulma anındaki stun < 40 ise kurtulur
    holderBreakStun: 1,       // §6 tutana gelen herhangi isabet (stunAdd ≥ 1) tutuşu koparır
    carryEscapeGraceTicks: 48,   // §6 omuzda ilk 0.8 s: basış sayılmaz (kullanıcı: "ilk belli bi süre tuşa bassa da kurtulamasın")
    carryEscapePresses: 6,       // §6 pencere sonrası 6 yüz tuşu basışı → kurtulur; basmazsa kucakta KALIR (zaman aşımı yok)
    carryEscapeRecoverTicks: 12, // §6 kurtulan kurbanın kısa toparlanması (0.2 s kontrol kilidi); tutanın 21 t sersemliğinden kısa
    breakStun: 20,            // §6 kurbana gelen isabet stunAdd ≥ 20 → koparır
    breakSeparation: 3,       // §4.1 G1 / §6 koparma / iki tutma aynı tick: 3 u/s ayrılma
    throwKb: 6.5,             // §4.1 G2 / §6 fırlatılan kb 6.5 (%-ölçekli, tumble tavanı)
    throwLift: 5,             // §4.1 G2 / §6 lift 5
    throwDmg: 12,             // §4.1 G2 fırlatılan dmg 12
    bodyImpact: { dmg: 5, stun: 25, kb: 8, lift: 2 },   // §4.1 G2 / §6 çarptığı rakibe (Hit_B)
    missOpenTicks: 18,        // §1/§4.1 G1 ıska r 18 t (0.30 s açık; tam r, donuk poz yok)
    pickUpRange: 1.2,         // §4.1 G3 / §11 / §12 yatan rakip ≤ 1.2 u
    pickUpGripTick: 16,       // §4.1 G3 kavrama 16. t (PickUp 0.53; kurban Carried olur)
    pickUpTotal: 38,          // §4.1 G3 toplam 16 + 22 = 38 t (0.63 s)
    escapeJump: 3,            // §6 AYAKTA tutuştan kurtulan Dodge_Backward ile 3 u geri (omuzdan kurtulmada yok)
    escapeIframes: 12,        // §6 ayakta kurtulma i-frame 12 t (omuzdan kurtulmada yok)
    holderStunTicks: 21,      // §6 kurtulmada / Carry koparmada tutan Hit_A 0.35 s = 21 t
    grabbedHolderStunTicks: 18,   // §4.1 G1 w/a'da isabet → tutan Hit_A 0.30 s = 18 t (WP0 ek)
    holdTurnTicks: 6,         // §4.1 G1b geri/yan fırlatmada tutan kurban etrafında 6 t'de döner (WP0 ek)
    throwReleaseTick: 19,     // §4.1 G2 bırakma 19 t (Throw 0.67 s ölçüm) (WP0 ek)
    liftAttachTick: 13,       // ANIMATION §6.3 omuza almada kurban 13. t'de sokete biner (WP0 ek)
  },
  EDGE: {
    brakeThreshold: 0.60,     // §3 çubuk dış bileşeni < 0.60 → kenarda durur; ≥ 0.60 düşer
    killY: -8,                // §8 kill plane y < −8 (0.62 s düşüş)
    fallWatchY: -4,           // §10 y < −4 olan dövüşçü 0.35 s daha kamerada
    fallWatchTicks: 21,       // §10 0.35 s = 21 t
    alarmDist: 2,             // §3/§11 kenara < 2 u: kırmızı halka (kendi karakteri); Tumble'da edgeDist < 0 → Ev.EdgeAlarm
    hudRingSelf: 2,           // §14 kendi karakterinde kırmızı zemin halkası < 2 u
    hudRingOthers: 1.5,       // §11/§14 rakiplerde soluk halka < 1.5 u
    keyBrakeMove: 0.55,       // Entegrasyon eki (3. tur): klavye (WASD) hareket tavanı alarm bölgesinde; brakeThreshold'un ALTINDA
                              // olmalı — WASD tam itme 1.0 ürettiği için klavye oyuncusu §3 kenar frenini alamıyordu. Bot
                              // `edgeClip` ile aynı sayı; pad çubuğu boşta değilse kırpma uygulanmaz (inputManager.ts).
  },
  ITEMS: {
    firstSpawn: 480,          // §9 maç başı 8 s
    interval: 720,            // §9 sonra her 12 s
    maxOnField: 2,            // §9 sahnede en fazla 2
    dropHeight: 3,            // §9 3 u yükseklikten düşer
    despawn: 1200,            // §9 yerde 20 s sonra yok olur
    blinkBefore: 180,         // §9 son 3 s yanıp söner
    weights: { '1h': 30, '2h': 20, dual: 20, staff: 15, shield: 15 },   // §9 tür ağırlığı (%)
    // Alma menzili 1.0 → 1.6 ve koni 80° → 180° (yani yön aranmıyor): kullanıcı "yerden silah almak çok zor,
    // pozisyonu denk getiremiyorsun" dedi. 1.0 u, kapsül yarıçapının (0.5) iki katı — silahın tam ayak dibinde
    // olmasını istiyordu; üstelik yukarıdan bakan kamerada mesafeyi kestirmek zor. 1.6 u tutma menziliyle aynı
    // (T.AIM.grabRange): "birini tutabildiğin mesafeden silahı da alabilirsin" tutarlı bir kural. Yere eğilip bir
    // şey almak için ona tam dönük olmak gerekmiyor → koni kapalı (cos 180° = −1, her yön geçer).
    pickupRange: 1.6,         // §9/§2 B, eşya ≤ 1.6 u (4. tur geri bildirimi; eski 1.0)
    pickupConeDeg: 180,       // §9 yön aranmaz (eski ±80°)
    pickupFirstRange: 0.9,    // §2 B önceliği: bu mesafedeki eşya YATAN RAKİPTEN de önce alınır ("üstünde duruyorsun")
    equipTicks: 14,           // §9 equip klip 0.53 s = 14 t (PickUp@0-0.65 tS 2.2)
    dropCooldown: 30,         // §9 düşen eşya 0.5 s alınamaz
    durabilityHits: 10,       // §9 10 isabet (blok dâhil)
    durabilitySec: 30,        // §9 veya 30 s
    throwSpeed: 16,           // §9 prop 16 u/s
    throwGravity: -8,         // §9 yerçekimi −8 (hafif yay), menzil ≈ 10 u
    throwSpin: 3,             // §9 3 tur/s
    throwRadius: 0.6,         // §9 hitbox r 0.6
    throwRadius2H: 0.8,       // §9 2H 0.8
    throwDmg: 10,             // §9 ilk temas dmg 10
    throwDmg2H: 13,           // §9 2H 13
    throwStun: 35,            // §9 stun 35
    throwStun2H: 45,          // §9 2H 45
    throwKb: 7,               // §9 kb 7 (9 → 7: %0'da tumble değil, Hit_B)
    throwLift: 3,             // §9 lift 3
    // §4.2/§9 sınıf çarpanları. NOT: §4.2 hamle tablolarındaki dmg/stun/kb değerleri bu çarpanları ZATEN içerir (dual hava X "A1 ×1.3"
    // örneği gibi); çarpanlar yalnız silahsız tablodan türetilen/paylaşılan hamlelere ve bot değerlemesine uygulanır (WP3 kararı).
    reachBonus: { unarmed: 0, '1h': 0.4, '2h': 0.7, dual: 0.3, staff: 1.0 },   // §4.2/§9 menzil +0.4 / +0.7; dual 0.98 uç (WP0 tahmini 0.3); asa uç 2.0
    dmgMult: { unarmed: 1.0, '1h': 1.3, '2h': 1.6, dual: 1.0, staff: 0.8 },     // §4.2 dmg ×1.3 / ×1.6 / asa ×0.8
    kbMult: { unarmed: 1.0, '1h': 1.0, '2h': 1.0, dual: 1.0, staff: 1.0 },      // §4.2 asa "kb ×1.2" kaldırıldı → tümü 1.0
    stunMult: { unarmed: 1.0, '1h': 1.0, '2h': 1.0, dual: 1.25, staff: 1.0 },   // §4.2 dual stun ×1.25
    // §4.2 / ANIMATION §7: L = katalog max.y + holder ofseti — assets/viewer/data/catalog.json'dan ELLE kopyalandı (kural 5; items.test
    // katalogla çapraz doğrular). Partial: kalkanlar yazılmaz, okuma `?? 0`.
    length: { sword_1handed: 1.41, dagger: 0.98, axe_1handed: 0.97, sword_2handed: 1.96, sword_2handed_color: 1.96, axe_2handed: 1.29, staff: 2.0 /* 1.25 + 0.75 */ },
    holderOffsetY: { staff: 0.75 },   // §4.2 asanın pivotu ortada → holder +0.75, uç 2.0
    propsByKind: {            // §9 / §4.2 doğabilecek proplar (WP0 ek; seçim hashNoise ile)
      '1h': ['sword_1handed', 'axe_1handed'],
      '2h': ['sword_2handed', 'sword_2handed_color', 'axe_2handed'],
      dual: ['dagger'],       // dual = iki dagger Item (sağ + sol)
      staff: ['staff'],
      shield: ['shield_round', 'shield_round_color', 'shield_square', 'shield_square_color', 'shield_spikes', 'shield_spikes_color', 'shield_badge', 'shield_badge_color'],
    },
    pickupTicks: 18,          // §9 PickUp@0-0.65 tS 2.2 = 0.30 s = 18 t (WP0 ek)
    dropUpMin: 1.5, dropUpMax: 2.5,     // §9 düşürme: yukarı 1.5–2.5 u/s (WP0 ek)
    dropBackMin: 1.5, dropBackMax: 2.5, // §9 geri 1.5–2.5 u/s (WP0 ek)
    dropGravity: -30,         // ANIMATION §7 düşen prop yerçekimi −30 (WP0 ek)
    dropSettleSec: 0.2,       // §9 0.2 s'de yatık yönelime slerp (WP0 ek)
    groundBobAmp: 0.05,       // ANIMATION §7 yerde Y salınımı ±0.05 u (WP0 ek)
    groundBobHz: 1,           // ANIMATION §7 1 Hz (WP0 ek)
    groundYawRate: 0.5,       // ANIMATION §7 yaw 0.5 rad/s (WP0 ek)
    throwReleaseTick: 19,     // §9 silah fırlatma bırakma 19 t (WP0 ek)
  },
  RESPAWN: {
    delayTicks: 90,           // §8 ring-out → 90 t (1.5 s) sonra yeniden doğuş
    spawnAirTicks: 60,        // §8 Spawn_Air tS 1.3 = 1.0 s
    controlAt: 54,            // §8 kontrol 54 t'de açılır
    protectionTicks: 90,      // §8 doğuş koruması 90 t (%50 saydam yanıp söner; itişme yok)
    countdownStep: 60,        // §13 "3 · 2 · 1 · DÖVÜŞ!" her adım 60 t
    countdownOverlapsSpawn: true,   // §13 "3, 2" iniş sırasında; toplam 3 s
    lives: 3,                 // §13 3 can
    attributionTicks: 180,    // §8 son 180 t (3 s) içindeki lastHitBy puan alır (WP0 ek)
    protectionBlinkAlpha: 0.5,   // §8 doğuş koruması %50 saydam (WP0 ek)
  },
  AIM: {
    attackRange: 3.2,         // §11 nişan yardımı menzil 3.2 u
    attackConeDeg: 60,        // §11 koni ±60°
    maxHeightDiff: 2.0,       // §11 dikey fark ≤ 2.0 u
    grabRange: 1.6,           // §11 tutma 1.6 u (G1'in tek menzil tanımı)
    grabConeDeg: 80,          // §11 tutma ±80°
    throwConeDeg: 35,         // §11 fırlatma/silah atma ±35°
    throwRange: 8,            // §11 8 u
    mouseFreshTicks: 120,     // §2 aimYaw: mouse son 120 t (2 s) içinde hareket ettiyse geçerli
    scoreDotWeight: 2,        // §11 skor = dot·2 − d/3.2 (WP0 ek)
  },
  INPUT: {
    bufferTicks: 9,           // §11 saldırı/zıplama/dodge tamponu 9 t (0.15 s); kendi hitstop'unda yaşlanmaz
    grabBufferTicks: 11,      // §11 tutma 11 t
    deadzone: 0.18,           // §2 pad ölü bölge 0.18
    heavyHoldTicks: 15,       // §2/§4 ıska takibi: Y basılı ∧ 15. t'de (Kick w + a) dokunmadıysa holdBranch
  },
  CAMERA: {
    pitchDeg: 26,             // §10 pitch 26°
    fovDeg: 40,               // §10 FOV 40° sabit (zoom = mesafe)
    margin: 1.5,              // §10 kenar payı 1.5 u
    distMult: 1.1,            // §10 8-köşe mesafesi ×1.1
    minDist: 10, maxDist: 28, // §10 clamp [10, 28]
    lambdaOut: 8,             // §10 uzaklaşma λ 8
    lambdaIn: 3,              // §10 yakınlaşma λ 3
    lambdaTarget: 6,          // §10 hedef nokta λ 6
    shakes: {                 // §10 tablo: genlik (u) / süre (s) / roll (°)
      light: { amp: 0.05, dur: 0.08 },                 // hafif isabet (yalnız oyuncu vuran/vurulan)
      heavy: { amp: 0.12, dur: 0.13 },                 // ağır isabet (3. şahıs ×0.3)
      knockdown: { amp: 0.18, dur: 0.22, roll: 1.5 },  // knockdown (herkes)
      parry: { amp: 0.06, dur: 0.22 },                 // parry (herkes)
      ringout: { amp: 0.30, dur: 0.30 },               // ring-out (herkes) + zoom-out
    },
    shakeOtherHeavyMult: 0.3, // §10 3. şahıs ağır isabet ×0.3 (hafif 0)
    shakeOtherKnockdownMult: 0.5, // §10 knockdown/parry(/throw/finisher) "herkes" → 3. şahıs ×0.5 (entegrasyon, orkestratör kararı 1: all-bot
                              //     duman 4/8 dövüşçüde sarsıntı oranı 0.28/0.45 idi; kabul 4 dövüşçüde < 0.30). Ring-out herkes ×1.
    shakeOtherCooldown: 0.25,  // Entegrasyon eki (3. tur): 3. şahıs (mult < 1) sarsıntıları arasındaki en küçük aralık (s) — shake()
                              //     "birikmez, max" kuralıyla her olay süreyi baştan başlattığı için 8 dövüşçüde kamera zamanın
                              //     ~%30'unda titriyordu. Oyuncuyu ilgilendiren ve ring-out sarsıntıları muaf.
    fallFocusFadeTicks: 18,   // Entegrasyon eki (3. tur): düşen dövüşçünün odak kutusuna katkısı 1 → 0 (t; 0.3 s). EDGE.fallWatchTicks
                              //     (21 t) ALTINDA olmalı — ağırlık, izleme penceresi kapanmadan 0'a insin (sert kadraj sıçraması yok).
    shakeDistScaleMax: 1.25,  // §10 ×clamp(dist/18, 1, 1.25)
    ringoutZoom: 0.08,        // §8/§10 ring-out mesafe +%8
    ringoutZoomDur: 0.15,     // §8/§10 0.15 s'de
    // 0: son can ring-out'undaki ağır çekim kaldırıldı (kullanıcı isteği). Maç BİTİŞİNDEKİ 2 s'lik yavaşlatma
    // (matchEndSlowMo) duruyor — o kazanan sunumunun parçası, düşme anı değil.
    lastLifeSlowMo: 0,        // §10 (eski 0.6 s ×0.5)
    slowMoScale: 0.5,         // §10/§13 yavaşlatma çarpanı
    slowMoMax: 3,             // §10 maçta en fazla 3 kez
    // §10 MİNİ AĞIR ÇEKİM (kullanıcı: "rakibi yere veya aşağıya düşüren hamle mini bi ağır çekim yapabilir mi, Party
    // Animals'daki gibi"). Ödül DÜŞÜŞÜN SONUCUNDA değil, İYİ VURUŞUN KENDİ ANINDA: isabet knockdown ürettiyse ya da
    // TAKLA attırdıysa (inişte yere serilir) hits.ts → match.ts tetikler; ayrıca kenar tahmini ARANMAZ.
    // Kaldırılan üç öğeyle (kenar flaşı, sahne donması, eleme ağır çekimi) KARIŞTIRMA: onlar "biri düştükten SONRA"
    // çalışıyordu ve oyunu takılıyor gibi gösteriyordu; bu, vuruşun kendi karesinde.
    // ZAMAN EĞRİSİ (5. tur; kullanıcı: "ağır çekim başladığında duruyor, sonra hızlanıyor. ben yavaşça aksın
    // istiyorum vuruş"). 4. turdaki eğri s(u) = 0.12 + 0.88·u² darbede neredeyse DURUYOR sonra hızlanarak çıkıyordu:
    // ekranda "don + fırla" okunuyordu, oysa istenen vuruşun SABİT ve yavaş AKMASI. Yeni eğri bir PLATO:
    //   u ≤ 1 − easeOut  → hold                     (vuruş burada akar: sabit ×0.25, yani 4 kat yavaş)
    //   sonrası          → smoothstep ile 1.0'a     (son %35'te yumuşak çıkış, sıçrama yok)
    // Girişte rampa YOK ve olmamalı: isabet karesinde zaten hitstop var (§11), zamanın oraya BASAMAKLA inmesi
    // "çat" hissini verir; rampa eklenirse darbe karesi tam hızda geçer, yani izlenmek istenen kare kaçar.
    //   u = 0 … 0.65 → 0.250 (12 tick boyunca sabit)   u = 0.72 → 0.333   u = 0.83 → 0.652   son tick → 0.949
    // Ekran süresi = Σ (1/60)/s(k), k = 0..17 → 1.01 s; bunun 0.80 s'si SABİT ×0.25'te geçer (eski eğride 0.30'un
    // altında geçen süre yalnız 0.67 s idi ve hiçbir anı diğerine benzemiyordu). Kalan 0.21 s çıkış rampası.
    // Süre SİM tick'i; 0 → kapalı.
    knockdownSlowMoTicks: 18,
    knockdownSlowMoHold: 0.25,      // §10 platonun hızı — vuruşun izlendiği sabit çarpan (maç bitişinin 0.5'inden AYRI)
    knockdownSlowMoEaseOut: 0.35,   // §10 pencerenin son %35'i: hold → 1.0 smoothstep (0 → basamakla kes)
    // Bekleme 180 t sim ≈ gerçek 3.6 s (yavaşlatmanın fazlası dâhil) → "özel an" seyrekliği korunur.
    knockdownSlowMoCooldownTicks: 180,   // §10 iki mini ağır çekim arası en az 180 t (3 s sim)
    // KAMERA YAKINLAŞMASI (4. tur): asıl eksik olan buydu — zaman yavaşlıyordu ama kadraj hâlâ TÜM dövüşçüleri
    // çerçeveliyordu, yani "vuruşu detaylı izlemek" mümkün değildi. Pencere boyunca kadraj vuran + yiyen ikilisinin
    // ortasına kayar ve mesafe ×0.60'a iner. Taban AYRI (6.5 u): normal minDist 10 ve tipik dövüş mesafesi zaten
    // 10'a kelepçeli olduğu için çarpan tek başına HİÇBİR ŞEY yapmıyordu. 6.5 u'da dikey görüş alanı 4.7 u →
    // dövüşçü ekran yüksekliğinin ~%38'i (normalde ~%25). hitSlowMoZoom = 1.0 → yakınlaşma kapalı.
    hitSlowMoZoom: 0.60,          // (artık yalnız belge/geri uyumluluk; mesafe ikilinin açıklığından hesaplanıyor)
    hitSlowMoSpanMult: 1.6,       // §10 yakın plan mesafesi = hitSlowMoMinDist + ikili açıklığı × bu (kalabalıktan bağımsız)
    hitSlowMoMinDist: 6.5,
    hitSlowMoBlendIn: 0.10,       // §10 giriş: 0.10 s'de %95 (darbe karesi 0.139 s gerçek sürüyor → yakınlaşma tam o anda biter)
    hitSlowMoBlendOut: 0.30,      // §10 çıkış: 0.30 s'de %95 — girişten yavaş, yoksa pencere biterken kadraj "pat" diye açılır
    shakeDistRef: 18,         // §10 dist/18 (WP0 ek)
    matchEndSlowMo: 2.0,      // §13 bitiş 2 s ×0.5 (WP0 ek)
  },
  FEEL: {
    squashHit: [1.12, 0.85, 1.12],     // §11 vurulan → 80 ms out-cubic
    squashLand: [1.06, 0.92, 1.06],    // §11 iniş 60 ms
    squashJump: [0.94, 1.08, 0.94],    // §11 zıplama 60 ms
    squashThrown: [0.9, 1.15, 0.9],    // §11 fırlatılan uçarken
    squashMs: 80,             // §11 vurulan squash süresi
    flashMs: 100,             // §11 vurulanda 100 ms emissive flaş
    jitterAmp: 0.06,          // §11 hitstop'ta vurulanın mesh'i ±0.06 u titrer
    jitterHz: 40,             // §11 40 Hz
    snapFrames: 3,            // §11 sunum snap: hamlenin ilk 3 karesi
    snapRateBonus: 0.2,       // §11 klip tS +%20
    squashLandMs: 60,         // §11 iniş 60 ms (WP0 ek)
    squashJumpMs: 60,         // §11 zıplama 60 ms (WP0 ek)
    controlFlashMs: 60,       // §5 TumbleControl beyaz flaş 60 ms (WP0 ek)
    fellShrinkSec: 0.25,      // §8 ring-out 0.25 s'de küçülerek kaybolur (WP0 ek)
    fellIdleRate: 0.6,        // §8 Jump_Idle tS 0.6 (kendi düştüyse) (WP0 ek)
    fellSpinDegPerSec: 540,   // §8 540°/s Y dönüşü (WP0 ek)
    eliminatedShrinkSec: 0.4, // §13 / ANIMATION §2.1 Death_A_Pose ölçek 1→0 (0.4 s) (WP0 ek)
  },
  BOT: {
    thinkTicks: 6,            // §12 karar her 6 t (10 Hz), id'ye göre kaydırmalı
    targetHysteresisTicks: 60,   // §12 hedef seçimi 1 s histeresis
    maxBotsPerTarget: { easy: 1, normal: 1, hard: 2 },   // §12 aynı hedefe en fazla bot
    lookaheadTicks: 18,       // §12 yerde 18 t ileri öngörü (kenar vetosu)
    parryTapLead: 3,          // §12 öngörülen impact −3 t'de L1 dokunuşu
    params: {                 // §12 zorluk tablosu (reactionTime s → tick: 0.40/0.24/0.14 → 24/14/8)
      easy: { reactionTicks: 24, aggression: 0.15, evasion: 0.10, parryChance: 0, gapAccuracy: 0.70, comboFinish: 0.30, edgeAwareness: 0.50, carryToEdge: 0.0, mashRate: 3, diAccuracy: 0, aimErrorDeg: 18, getUpDelayTicks: 60, wakeKickChance: 0 },
      normal: { reactionTicks: 14, aggression: 0.32, evasion: 0.30, parryChance: 0.05, gapAccuracy: 0.88, comboFinish: 0.70, edgeAwareness: 0.80, carryToEdge: 0.4, mashRate: 5, diAccuracy: 0.5, aimErrorDeg: 9, getUpDelayTicks: 30, wakeKickChance: 0.2 },
      hard: { reactionTicks: 8, aggression: 0.50, evasion: 0.55, parryChance: 0.20, gapAccuracy: 0.97, comboFinish: 0.95, edgeAwareness: 0.95, carryToEdge: 0.9, mashRate: 8, diAccuracy: 0.9, aimErrorDeg: 3, getUpDelayTicks: 30, wakeKickChance: 0.4 },
    },
    threatRange: 1.8,         // §12 2. tehdit: 1.8 u içinde windup (WP0 ek)
    lyingRollRange: 2.5,      // §12 0.a rakip ≤ 2.5 u ve yaklaşıyorsa Roll (WP0 ek)
    wakeKickRange: 1.5,       // §12 0.c rakip ≤ 1.5 u → kalkış tekmesi (WP0 ek)
    itemCrawlRange: 1.0,      // §12 0.d eşya ≤ 1.0 u (WP0 ek)
    itemCrawlEnemyMin: 3.0,   // §12 0.d rakip ≥ 3 u (WP0 ek)
    carryEdgeMax: 7,          // §12 3. yatan rakip kenara ≤ 7 u → kaldır-taşı-fırlat (WP0 ek)
    finishStun: 55,           // §12 3. rakip stun ≥ 55 → Y ailesi (WP0 ek)
    weaponSeekRange: 5,       // §12 3. yerde silah ≤ 5 u (WP0 ek)
    weaponSeekEnemyMin: 3,    // §12 3. rakip ≥ 3 u (WP0 ek)
    scoreDmg: 0.4,            // §12 4. puan: 0.4·(hasarı yüksek) (WP0 ek)
    scoreArmed: 0.5,          // §12 4. 0.5·(silahlı) (WP0 ek)
    scoreEdge: 0.3,           // §12 4. 0.3·(kenara yakın) (WP0 ek)
    scoreRevenge: 1.3,        // §12 4. ×1.3 beni son vuran (WP0 ek)
    dashChance: 0.3,          // §12 5. koşma bağlamında %30 Dash Tackle (WP0 ek)
    flyingKickMinHit: 0.6,    // §12 5. uçan tekme yalnız isabet olasılığı > 0.6 (WP0 ek)
    whiffWaitTicks: 24,       // §12 5. whiff sonrası 24 t bekler (WP0 ek)
    strafeDist: 2.5,          // §12 6. ≥ 2.5 u'da rastgele strafe (WP0 ek)
    tauntChance: 0.25,        // §12 7. rakip ring-out olunca %25 (WP0 ek)
    tauntChanceEasy: 0.5,     // §12 7. easy %50 (WP0 ek)
    tauntEdgeMin: 3,          // §12 7. yalnız kenara > 3 u (WP0 ek)
    edgeClip: 0.55,           // §12 1. / ARCHITECTURE §8 dış bileşen 0.55'e kırpılır (fren eşiği altı) (WP0 ek)
    reevalAfterHitTicks: 24,  // §12 2. vurulmadan 0.4 s sonra tekrar değerlendirir (WP0 ek)
    recoveryMargin: 1.5,      // §12 1. hedef en yakın kenar +1.5 u (WP0 ek)
    predictSec: 0.3,          // ARCHITECTURE §8 kenar vetosu pred = pos + vel·0.3 + intent·runSpeed·0.3 (WP0 ek)
  },
  WEIGHT: {                   // §5 weightMult (görsel kütle ±%10)
    Knight: 0.90, Barbarian: 0.90, Skeleton_Warrior: 0.90,
    Mage: 1.00, Ranger: 1.00, Rogue: 1.00, Rogue_Hooded: 1.00, Mannequin_Medium: 1.00,
    Skeleton_Rogue: 1.10, Skeleton_Mage: 1.10, Skeleton_Minion: 1.10,
  },
  ANIM: {                     // ANIMATION §2.1 / §6 (hamle dışı durumlar; WP0 ek grubu)
    walkMin: 0.3, runAMin: 1.5, runBMin: 4.0,   // §3 loko eşikleri v < 0.3 idle · 0.3–1.5 Walk · 1.5–4.0 Run A · ≥ 4.0 Run B
    naturalWalkA: 0.77,       // §3 Walking_A doğal hız (ölçüm, temas yöntemi)
    naturalRunA: 3.0,         // §3 Running_A doğal hız (tahmin)
    naturalRunB: 4.8,         // §3 Running_B doğal hız (tahmin)
    naturalWalkBack: 0.66,    // ANIMATION §2.1 Walking_Backwards (tahmin)
    naturalStrafe: 2.1,       // ANIMATION §2.1 Running_Strafe_* (tahmin)
    naturalCrawl: 0.7,        // ANIMATION §2.1 Crawling (tahmin; 1.5/0.7 ≈ 2.1 → clamp 1.8)
    walkRate: [0.6, 2.0],     // ANIMATION §2.1 Walking_A tS clamp
    runARate: [0.5, 1.6],     // ANIMATION §2.1 Running_A tS clamp
    runBRate: [0.8, 1.5],     // ANIMATION §2.1 Running_B tS clamp
    crawlRateMax: 1.8,        // §4.1 Crawling tS 1.8
    footWalkA: [0.00, 0.52], footRunA: [0.07, 0.47], footRunB: [0.10, 0.50],   // ANIMATION §2.1 ayak temasları L/R (ölçüm)
    rollTotalDeg: 360,        // Entegrasyon eki (3. tur): yerde rulo toplam açısı — animMap (rollDeg) ve fighterView (pivot dönüşü)
                              // AYNI sayıyı okusun (ANIMATION §6.2 "180°" ile deviation; gerekçe fighterView.ts'de: Lying sırtüstü).
    fadeIdle: 0.20,           // ANIMATION §2.1 Idle giriş 0.20
    fadeLoko: 0.15,           // ANIMATION §2.1 Walk/Run 0.15
    fadeAttackIn: 0.05,       // ANIMATION §2.2 saldırıya giriş 0.05
    fadeComboIn: 0.04,        // ANIMATION §2.2 kombo içi 0.04
    fadeAttackOut: 0.10,      // ANIMATION §2.2 saldırıdan çıkış 0.10
    fadeHitIn: 0.03,          // ANIMATION §6.1 Hit giriş 0.03
    fadeHitOut: 0.10,         // ANIMATION §6.1 hitstun bitince loko'ya 0.10
    fadeStage: 0.04,          // §4 aşama geçişi 0.04
    fadeStageG3: 0.06,        // §4.1 G3 / D2 aşama geçişi 0.06
    fadeUpperIn: 0.05,        // ANIMATION §4 üst katman giriş 0.05 (base → #lower 0.05)
    fadeUpperCarry: 0.12,     // ANIMATION §4 Carry üst katman 0.12
    additiveDecay: 0.20,      // ANIMATION §6.1 Hit_A+ 0.20 s'de sönümlenir
    fadeLyingIdle: 0.20,      // ANIMATION §6.2 Death_A_Pose → Lie_Idle 0.20
    fadeDodgeOut: 0.12,       // ANIMATION §2.1 Dodge çıkış 0.12
    fadeDodgeFIdle: 0.15,     // ANIMATION §2.1 Dodge_F son pozu → Idle 0.15
    idleOffsetMax: 1.0,       // ANIMATION §2.1 idle rastgele ofset 0–1.0 s (bot senkronu kırar)
    idleBEverySec: 30,        // ANIMATION §2.1 Idle_B 30 s'de bir tek tur
    jumpStartAt: 0.30,        // §3 / ANIMATION §2.1 Jump_Start ofset 0.30 (çömelme atlanır)
    jumpStartRate: 1.2,       // §3 tS 1.2
    jumpStartTicks: 8,        // ANIMATION §2.1 8 t sonra JumpAir
    jumpLandRate: 1.3,        // ANIMATION §2.1 Jump_Land tS 1.3
    fastFallPitchDeg: 10,     // ANIMATION §2.1 FastFall root pitch +10°
    fastFallPitchTicks: 4,    // ANIMATION §2.1 4 t'de
    hitARef: 0.53, hitARateMax: 1.8,   // §5 Hit_A tS = clamp(0.53/hitstun, 1.0, 1.8)
    hitBRef: 0.68, hitBRateMax: 1.6,   // §5 Hit_B tS = clamp(0.68/hitstun, 1.0, 1.6)
    flinchWeight: 0.8,        // §5 / ANIMATION §6.1 arkadan hafif darbe additive w 0.8
    flinchWeightArmor: 0.6,   // ANIMATION §6.1 süper zırh altında w 0.6
    flinchArmorSec: 0.15,     // ANIMATION §6.1 0.15 s
    launchRate: 1.5,          // §5 Launch Death_A [0–0.267] tS 1.5
    launchPauseAt: 0.267,     // §5 pause @0.267
    launchPitchMin: -15, launchPitchMax: -50,   // §5 root pitch −15°…−50° (vy'ye göre)
    tumblePivotY: 1.1,        // ANIMATION §6.6 spinPivot (0, 1.1, 0)
    tumbleMeshOffset: [0, -0.2, 0.3],   // ANIMATION §6.6 visualRoot ofseti (0, −0.2, +0.3)
    knockdownRate: 1.0,       // §6 Death_A@0.37-0.55 tS 1.0
    getUpRate: 2.05,          // §6 Lie_StandUp@0.70-2.33 tS 2.05
    quickGetUpRate: 1.6,      // §4.1 Lie_StandUp@1.60-2.33 tS 1.6
    getUpKickRate: 2.0,       // §4.1 kalkış tekmesi aşama 1 tS 2.0
    spawnRate: 1.3,           // §8 Spawn_Air tS 1.3
    spawnLandAt: 0.83,        // ANIMATION §2.1 iniş klip 0.83 (638 ms toz)
    blockEnterRate: 1.5,      // §3 Melee_Block tS 1.5
    blockHitRate: 2.0,        // §3 Melee_Block_Hit tS 2.0
    guardBreakRate: 1.33,     // §3 guard break Melee_Block_Hit tam tS 1.33 (48 t)
    parryStunRate: 1.3,       // §7 saldıran Hit_B tS 1.3
    liftRate: 1.5,            // §4.1 G1b Interact@0.65-1.30 tS 1.5
    pickUpItemRate: 2.2,      // §9 PickUp@0-0.65 tS 2.2
    pickUpRate: 1.4,          // §4.1 G3 PickUp dilimleri tS 1.4
    tauntRate: 1.2,           // §4.1 T Cheering tS 1.2 (Waving/Skeletons_Taunt 1.0)
    heldPauseAt: 0.20,        // §4.1 G1b / ANIMATION §6.3 kurban Hit_A 0.20'de paused
    holdPauseAt: 0.57,        // ANIMATION §6.3 tutan Interact 0.57 karesinde paused
    attachFwd: 0.55, attachUp: -0.20, attachSide: 0.30, attachYawDeg: 90,   // §4.1 C / ANIMATION §6.5 chest + (x 0.30, y −0.20, z 0.55), yaw +90°
    carriedRollDeg: 8, carriedRollHz: 3,   // §4.1 C roll ±8°·sin(2π·3t)
    pickUpAttachFrom: [0.45, -0.75, 0.30],   // ANIMATION §6.5 PickUp senkronu başlangıç (fwd, up, side)
    pickUpAttachTicks: 22,    // ANIMATION §6.5 lerp 22 t
    throwAttachTo: [0.20, 0.30, 0.30],       // ANIMATION §6.4 Throw boyunca hedef (fwd, up, side)
    throwAttachTicks: 14,     // ANIMATION §6.4 0–14 t
    upperMaxHipsYawDeg: 25,   // ANIMATION §4 hipsYawRange > 25° → #upper üretilmez
  },

  /* ─────────────────────────────── AĞ (NETWORK.md §3.3) ─────────────────────────────── */
  NET: {
    profile: 'region',            // denge hedefi: aynı bölge ~40-60 ms (NETWORK.md kararı 5)
    profiles: {
      lan:    { inputDelayTicks: 1, rollbackTicks: 3,  rttMaxMs: 10 },
      region: { inputDelayTicks: 2, rollbackTicks: 18, rttMaxMs: 80 },
      global: { inputDelayTicks: 3, rollbackTicks: 12, rttMaxMs: 260 },
    },
    maxInputDelayTicks: 4,        // §3.3 türetmesi: 14 t tepki + d ≤ 17 t → d ≤ 3, tavan 4
    maxRollbackTicks: 22,         // 367 ms; yerel-önce oynatmada tahmin derinliği 1 + inputDelay + ahead(≤12)
    catchUpMaxSteps: 12,          // 8 dövüşçüde 12 tick ≈ 0.6 ms (çevrimdışı MAX_STEPS 4 ve artık atılır)
    resyncBehindTicks: 90,        // 1.5 s geriye düşen istemci yetişmeye çalışmaz, tam durum ister
    disconnectGraceTicks: 240,    // 4 s nötr girdi, sonra bot devralır (NETWORK.md kararı 6)
    // Kalp atışı: sunucu her koltuğa `ping` atar, istemci `pong` döner. İKİ işi var —
    //   1. HUD'daki gecikme göstergesi (oyuncu kendi ve rakiplerinin ping'ini görür),
    //   2. ÖLÜ BAĞLANTI AYIKLAMA: TCP yarı-açık kalabilir (dizüstü kapandı, ağ gitti); soket kapanmadığı
    //      için koltuk sonsuza dek dolu görünür ve oda kilitlenir. Bu üretimde yaşandı.
    pingPeriodSec: 1,             // 1 Hz: ölçüm için yeterince sık, trafiği kabartmayacak kadar seyrek
    pingTimeoutSec: 20,           // bu kadar süredir pong yoksa bağlantı ölü sayılır (20 pencere kaçırıldı)
    rttSmoothing: 0.3,            // EMA: tek bir geç paket göstergeyi zıplatmasın
    // ── oynatma saati ──
    // ÖLÇÜM (üretim, 2026-09-04): sunucu yayını KENDİ üstünde kusursuz düzgün (paket arası p50 17.1 ms,
    // p99 18.8 ms, 40 ms üstü hiç yok). Aynı akış istemciye vardığında öbekleniyor: p99 102 ms, saniyede ~2 kez
    // ~100 ms hiç paket gelmiyor, sonra 6-7 tanesi birden düşüyor. Tarayıcısız düz bir Node istemcisinde de
    // aynı — yani suç oyunda değil, yolda (WiFi güç tasarrufu ~100 ms'de bir biriktirip boşaltır; tipik).
    // İstemci bunu YUTMAK zorunda: sim yerel saatle akar, sapma aşağıdaki denetleyiciyle düzeltilir.
    // YEREL-ÖNCE OYNATMA. Dünya, KENDİ girdimizin yazıldığı tick'te koşar: `1 + inputDelay + ahead`.
    // Böylece bastığın kare karakterin oynar — çevrimdışındaki his. Rakipler o derinlikte TAHMİN edilir,
    // otoriter girdi gelince geri sarılıp düzeltilir; düzeltmeler göze sıçratılmaz, `correctionSmoothSec`
    // içinde eritilir (bkz. view/fighterView.ts).
    //
    // Buradaki `leadTicks` artık o türetilmiş hedefe eklenen İNCE AYAR: 0 = tam girdi tick'i, negatif =
    // biraz daha güvenli (daha az tahmin, biraz daha gecikme).
    //
    // Ölçülen tarihçe: hedef 0 iken her tick tahmin edilip geri sarılıyordu (77 ms'de 1432 geri sarma) →
    // −7'lik tampon geri sarmayı sıfırladı ama girdi gecikmesini 17 tick'e (283 ms) çıkardı, bir dövüş
    // oyunu için kabul edilemez. Doğru cevap ikisi de değil: yerel-önce + görsel düzeltme yumuşatma.
    // ESKİ AÇIKLAMA (tampon dönemi), tarihçe için:
    //
    // Sıfırdı ve ölçüm şunu gösterdi: istemci tam da alınan son tick'in üstünde durunca bir tick daha
    // ilerlemek için HER SEFERİNDE tahmin etmek zorunda kalıyor; paket gelince tahmin tutmuyor (botların ve
    // rakiplerin girdisi sürekli değişiyor) ve HER TİCK geri sarılıyor. 77 ms ping'li bir maçta: 1432 geri
    // sarma, 17169 yeniden koşulan tick, karelerin %37'sinde düzeltme → "yürürken bile takıla takıla".
    // Üstelik bu tahminin faydası da yoktu: kendi girdimiz zaten inputDelay + ahead kadar ileri planlanıyor,
    // yani tahmin YALNIZ başkalarını kestiriyordu — en kötü olduğu iş.
    //
    // Negatif hedef bir GECİKME TAMPONU: koşulan her tick'in otoriter girdisi elimizde, tahmin devreye
    // girmiyor, geri sarma olmuyor. Bedeli tamponun kendisi (7 tick ≈ 117 ms görüntü gecikmesi); ölçülen
    // ~100 ms'lik paket öbeklenmesini yutacak kadar. Tahmin makinesi duruyor — tampon boşalırsa emniyet ağı.
    leadTicks: 0,
    /**
     * Oynatma hedefinin TABANI (tick). `ahead` uyarlaması sıfıra kadar inebiliyordu: ölçümde dünya akıştan
     * yalnız 3 tick (50 ms) ileri koşuyordu, oysa gidiş-dönüş 62 ms. Yani kendi girdimizin sunucuya
     * yetişmek için hiç payı kalmıyor; en ufak jitterde otorite onu KAÇIRIYOR, nötr sayıyor ve geri sarma
     * bizi geri çekiyor — oyuncunun en çok hissettiği hata ("geriye alıyor").
     *
     * Taban ölçülen öbeklenmeye göre: tek yön ~2 tick + ~100 ms jitter ≈ 6 tick pay.
     * Bedeli rakibi o derinlikte tahmin etmek; ama insan kendi karakterini hisseder, rakibin küçük
     * düzeltmesini değil.
     */
    minAheadTicks: 4,
    // Düzeltme yumuşatma eşikleri ÖLÇÜMLE kondu (üretim, 5 s, 4 bot, 60 ms ping):
    //   kendi dövüşçüm  → toplam 5.4 birim, en büyüğü 1.5
    //   rakipler        → toplam 78–142 birim, en büyüğü 5.8–13.2
    // Yani derin tahmin edilen RAKİPLER metrelerce düzeltiliyor; ilk denenen 1.2 birimlik tavan bunların
    // çoğunu "gerçek ışınlanma" sayıp sıçratıyordu. Tavan arena ölçeğine çekildi (ring-out/doğuş hâlâ dışarıda
    // kalsın diye 6), süre de gözün fark edemeyeceği bir kaymaya uzatıldı.
    /** Geri sarma düzeltmesi göze SIÇRATILMAZ: bu zaman sabitiyle eritilir (s). */
    correctionSmoothSec: 0.20,
    /** Bundan büyüğü düzeltme değil GERÇEK ışınlanmadır (doğuş, ring-out) — yumuşatılmaz (birim). */
    correctionMaxUnits: 6,
    clockEma: 0.02,               // hata ortalaması (~1 s): öbek başına ±6'lık zıplama hıza yansımasın
    clockGain: 0.02,              // tick başına hız düzeltmesi — 1 tick sapma → %2
    // ÖLÜ BANT: sunucunun tick'i öbek öbek öğrenildiği için yarım öbekten (~3 tick) daha ince bilinemez.
    // Bu kadarını KOVALAMAK, hızı sürekli 1'in az altında/üstünde tutuyor ve ~0.6 s'de bir kareyi 0 ya da
    // 2 tick yapıyordu (ölçüldü). Bant içinde hız TAM 1: sim mükemmel düzgün akar, düzeltme yalnız gerçek
    // kayma için devreye girer. Geçiş sürekli (bant kenarında sıçrama yok).
    // Ölü bant DARALDI: yerel-önce oynatmada hedefi aşmak boşuna tahmin derinliği demek (ölçüm: hedef 3
    // iken dünya 7 tick ileri gidiyordu, aradaki fark doğrudan fazladan geri sarma).
    deadbandTicks: 1,
    rateMin: 0.94,                // hız kırpması: düzeltme GÖRÜNMESİN (±%6 fark edilmez, sıçrama edilir)
    rateMax: 1.06,
    // ── bağlanma ──
    // Zaman aşımı `open` üstüne DEĞİL `welcome` üstüne kurulu: vekil (Caddy) soketi kabul edip arkadaki oyun
    // sunucusu ölü olabilir — o durumda `open` gelir, oyun hiç başlamaz ve ekran sonsuza dek "bağlanılıyor"
    // yazardı. Ölçüt, sunucunun KENDİSİNİN konuştuğunu görmek.
    connectTimeoutMs: 8000,
    roomBusyRetryMs: 3000,        // sunucuda maç sürerken otomatik yeniden deneme aralığı
  },
};
